import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Stack } from '@mantine/core';

import { usePostOrganisation } from 'api/organisations';
import { Form, TextField } from 'components/shared';
import { DYNAMIC_URLS } from 'consts';
import { useShowNotification } from 'hooks';

type Props = {
  opened: boolean;
  onClose: () => void;
};

type FormData = {
  name: string;
  boardName: string;
};

export const OrganisationCreateModal = ({ opened, onClose }: Props) => {
  const form = useForm<FormData>();
  const { mutateAsync: postOrganisation } = usePostOrganisation();
  const navigate = useNavigate();
  const { isSubmitting } = form.formState;
  const showNotification = useShowNotification();
  const { t } = useTranslation('organisations');

  const onSubmit = async (data: FormData) => {
    try {
      const { id } = await postOrganisation(data);
      showNotification({
        variant: 'success',
        message: t('organisationCreateModal.successMessage'),
      });
      const url = DYNAMIC_URLS.organisationDetails({
        organisationId: id.toString(),
      });
      navigate(url);
      onClose();
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t('organisationCreateModal.errorMessage'),
      });
    }
  };

  const preventedOnClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={preventedOnClose}
      centered
      size="md"
      title={t('organisationCreateModal.title')}
    >
      <Form form={form} onSubmit={onSubmit}>
        <Stack spacing="sm" mb="lg">
          <TextField
            name="name"
            label={t('organisationCreateModal.fields.name')}
            withErrorWrapperAlwaysEnabled={false}
          />
          <TextField
            name="boardName"
            label={t('organisationCreateModal.fields.boardName')}
            withErrorWrapperAlwaysEnabled={false}
          />
        </Stack>
        <Flex justify="flex-end" gap="lg">
          <Button variant="default" onClick={onClose}>
            {t('organisationCreateModal.cancelButton')}
          </Button>
          <Button type="submit" color="green" loading={isSubmitting}>
            {t('organisationCreateModal.createButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
